import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FieldSearchIcon } from "./labIcons";

import LabTestCatalogService from "./LabTestCatalogService";

/**
 * Debounced fuzzy-search dropdown for the lab-test catalog. On pick, hands
 * the parent the full snapshot (`{sltCode, ltCode, name, panel, unit,
 * refRange}`). Free-text is allowed — parent gets `{name, panel: "",
 * sltCode: "", ...}` and the flag falls back to `unknown` on save.
 */
const DEBOUNCE_MS = 250;

const dedupeBySltCode = (rows) => {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const key = ((r && r.sltCode) || (r && r.name) || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
};

const emptyTest = { sltCode: "", ltCode: "", loincCode: "", name: "", panel: "", unit: "", refRange: {} };

const LabTestCatalogAutocomplete = ({ value, sltCode, onChange, disabled }) => {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);
  const serviceRef = useRef(null);
  /**
   * Monotonic request id. LabTestCatalogService.search is 2 parallel GETs plus
   * a conditional bulk POST, so an EARLIER query routinely lands AFTER a later
   * one — without this stamp the stale response overwrites the fresh list (or
   * reopens a dismissed dropdown over an emptied field).
   */
  const reqIdRef = useRef(0);

  if (!serviceRef.current) serviceRef.current = new LabTestCatalogService();

  // Unmount: kill the pending debounce (Cancel inside the 250ms window used to
  // still fire the network round trips) and invalidate any in-flight response.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      reqIdRef.current += 1;
    },
    []
  );

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleType = (e) => {
    const q = e.target.value;
    setQuery(q);
    // Free-text edit drops the catalog link — parent's sltCode/ltCode/panel
    // reset so the row saves as free-text.
    onChange({ ...emptyTest, name: q });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const myId = ++reqIdRef.current;
    if (!q.trim()) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      serviceRef.current.search(
        q,
        (data) => {
          if (myId !== reqIdRef.current) return; // a newer query owns the box
          setResults(dedupeBySltCode(Array.isArray(data) ? data : []));
          setOpen(true);
          setLoading(false);
        },
        () => {
          if (myId !== reqIdRef.current) return;
          setResults([]);
          setLoading(false);
        }
      );
    }, DEBOUNCE_MS);
  };

  const pick = (item) => {
    setQuery(item.name || "");
    setOpen(false);
    onChange({
      sltCode: item.sltCode || "",
      ltCode: item.ltCode || "",
      loincCode: item.loincCode || "",
      name: item.name || "",
      panel: item.panel || "",
      unit: item.unit || "",
      refRange: item.refRange || {},
    });
  };

  return (
    <div className="cc-icd" ref={boxRef}>
      <div className="sec-search">
        <span className="sec-search-icon"><FieldSearchIcon /></span>
        <input
          type="text"
          value={query}
          onChange={handleType}
          onFocus={() => query && results.length > 0 && setOpen(true)}
          placeholder="Search tests, e.g. Hemoglobin"
          disabled={disabled}
        />
      </div>
      {/* Dropdown per 3041:12014 "Investigation results": name-only rows —
          the board draws no panel tag here (the Panel · Auto field below is
          where the parent shows up once picked). */}
      {open && (
        <ul className="li3-dd" role="listbox">
          {loading && <li className="li3-dd-empty">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="li3-dd-empty">
              No matches — press Save to add as free text.
            </li>
          )}
          {!loading &&
            results.map((r) => (
              <li
                key={r.sltCode || r.name}
                role="option"
                aria-selected={!!sltCode && r.sltCode === sltCode}
                className={`li3-dd-opt${sltCode && r.sltCode === sltCode ? " is-active" : ""}`}
                onClick={() => pick(r)}
              >
                {r.name}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

LabTestCatalogAutocomplete.propTypes = {
  value: PropTypes.string,
  sltCode: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default LabTestCatalogAutocomplete;
